/** @file checkMethods: lint, pick and stats — the checks an agent runs after a batch. */
import type { WarningCode } from '@zilo/pose3d-format/types/warning';
import type { PixelTile } from '@zilo/pose3d-format/validate/validatePixelTile';
import type { LintReply, PickBody, PickReply, StatsReply } from '../types/restAdded';
import type { ClientOptions } from './clientOptions';
import { fillPath } from './fillPath';
import { request } from './request';

/** The check members of the client. */
export type CheckMethods = {
  /**
   * `GET /api/v1/puppets/{id}/lint`; `clip` lints that clip too, `'*'` every clip; `ignore` drops warning codes;
   * `expectTile` (the game's pixel-profile.json tile) adds the CAMERA_MISMATCH tile check.
   */
  lint: (puppetId: string, q?: { clip?: string; ignore?: WarningCode[]; expectTile?: PixelTile }) => Promise<LintReply>;
  /** `POST /api/v1/puppets/{id}/pick`: the node and drawable at a world point. */
  pick: (puppetId: string, body: PickBody) => Promise<PickReply>;
  /** `GET /api/v1/puppets/{id}/stats`. */
  stats: (puppetId: string) => Promise<StatsReply>;
};

const PUPPET = '/api/v1/puppets/{id}';

/** Check calls over `opts`. */
export function checkMethods(opts: ClientOptions): CheckMethods {
  return {
    lint: async (id, q = {}) => (await request<LintReply>(opts, {
      method: 'GET', path: fillPath(`${PUPPET}/lint`, { id }), accept: 'json', query: {
        clip: q.clip, ignore: q.ignore === undefined || q.ignore.length === 0 ? undefined : q.ignore.join(','),
        expectTile: q.expectTile === undefined ? undefined : `${q.expectTile.w},${q.expectTile.h},${q.expectTile.hWall}`,
      },
    })).value,
    pick: async (id, body) => (await request<PickReply>(opts, { method: 'POST', path: fillPath(`${PUPPET}/pick`, { id }), body, accept: 'json' })).value,
    stats: async (id) => (await request<StatsReply>(opts, { method: 'GET', path: fillPath(`${PUPPET}/stats`, { id }), accept: 'json' })).value,
  };
}
